import React, { Fragment, useState, useEffect } from 'react';
import { navigate, graphql, Link } from 'gatsby';
import { Dialog, Transition } from '@headlessui/react';
import axios from 'axios';
import clsx from 'clsx';

import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

// Constants for tag display names
const tagDisplayNames = {
  People: 'Menschen',
  Society: 'Gesellschaft',
  Planet: 'Planet',
  Alle: 'Alle',
};

// Function to get CSS classes based on tag name
const getTagClass = (tag) => {
  switch (tag) {
    case 'People':
      return 'bg-yellow';
    case 'Society':
      return 'bg-orange';
    case 'Planet':
      return 'bg-green';
    default:
      return 'bg-purple text-white';
  }
};

// Function to extract internal tags (name, age, state) from video tags
const extractInternalTags = (tags) => {
  const internalTags = {
    age: null,
    name: null,
    state: null,
    age2: null,
    name2: null,
    age3: null,
    name3: null,
  };

  tags.forEach((tag) => {
    if (tag.name.startsWith('Age:')) {
      internalTags.age = tag.name.replace('Age:', '').trim();
    } else if (tag.name.startsWith('Name:')) {
      internalTags.name = tag.name.replace('Name:', '').trim();
    } else if (tag.name.startsWith('State:')) {
      internalTags.state = tag.name.replace('State:', '').trim();
    } else if (tag.name.startsWith('Age2:')) {
      internalTags.age2 = tag.name.replace('Age2:', '').trim();
    } else if (tag.name.startsWith('Name2:')) {
      internalTags.name2 = tag.name.replace('Name2:', '').trim();
    } else if (tag.name.startsWith('Age3:')) {
      internalTags.age3 = tag.name.replace('Age3:', '').trim();
    } else if (tag.name.startsWith('Name3:')) {
      internalTags.name3 = tag.name.replace('Name3:', '').trim();
    }
  });

  return internalTags;
};


// Function to format duration in minutes and seconds
const formatDuration = (duration) => {
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds} min`;
};

// Function to validate form inputs
const validate = (formData) => {
  const newErrors = {};
  if (!formData.name) newErrors.name = 'Name ist erforderlich';
  if (!formData.email) newErrors.email = 'E-Mail ist erforderlich';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
    newErrors.email = 'E-Mail ist ungültig';
  return newErrors;
};

// Function to format OTP countdown timer
const formatCountdown = (otpCountdown) => {
  const minutes = Math.floor(otpCountdown / 60);
  const seconds = otpCountdown % 60;
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

const VideoTemplate = ({ pageContext, data }) => {
  const { video, slugs = [], slug } = pageContext;

  // Extract internal tags from video tags
  const { name, age, state, name2, age2, name3, age3 } = extractInternalTags(video.tags);


  // Filter out internal tags to display only relevant tags
  const displayTags = video.tags.filter(
    (tag) =>
      !tag.name.startsWith('Age:') &&
      !tag.name.startsWith('Name:') &&
      !tag.name.startsWith('Age2:') &&
      !tag.name.startsWith('Name2:') &&
      !tag.name.startsWith('Age3:') &&
      !tag.name.startsWith('Name3:') &&
      !tag.name.startsWith('State:')
  );

  // Function to handle random video navigation
  const handleRandomVideo = () => {
    const filteredSlugs = slugs.filter((s) => s !== slug);
    const randomSlug =
      filteredSlugs[Math.floor(Math.random() * filteredSlugs.length)];
    navigate(`/videos/${randomSlug}`);
  };

  // Copy link functionality
  const [buttonText, setButtonText] = useState('Link kopieren');
  const [notificationVisible, setNotificationVisible] = useState(false);

  const handleButtonClick = () => {
    if (typeof window !== 'undefined') {
      const currentURL = window.location.href;

      navigator.clipboard.writeText(currentURL).then(
        () => {
          setButtonText('Kopiert');
          setNotificationVisible(true);

          setTimeout(() => {
            setButtonText('Link kopieren');
            setNotificationVisible(false);
          }, 3000);
        },
        (err) => {
          console.error('Async: Could not copy text: ', err);
        }
      );
    }
  };
  const formatNames = (names) => {
    if (names.length === 1) return names[0]; // Only one name
    if (names.length === 2) return `${names[0]} und ${names[1]}`; // Two names, use "und" between them

    // More than two names, use comma and "und" for the last separator
    return `${names.slice(0, -1).join(', ')} und ${names[names.length - 1]}`;
  };

  return (
    <Layout>
      <Header noButton={true} videoDetails={true} data={data} />
      <section className="mot__video">
        <div className="w-full space-y-3 md:space-y-6 text-center max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3">
            {displayTags.map((tag) => (
              <span
                key={tag.uri}
                className={`text-black text-base/[1.1] font-semibold px-2 py-1 pb-1.5 ${getTagClass(
                  tag.name
                )}`}
              >
                {tagDisplayNames[tag.name] || tag.name}
              </span>
            ))}
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-mono">
            {video.name}
          </h1>

          {(name || age || state) && (
            <p className="text-base md:text-lg xl:text-xl max-w-3xl mx-auto text-white/60">
              {/* Display Name and Age */}
              {formatNames(
                [
                  name && age && `${name} (${age})`,
                  name2 && age2 && `${name2} (${age2})`,
                  name3 && age3 && `${name3} (${age3})`,
                ].filter(Boolean)
              )}

              {/* Conditionally display State if present */}
              {state && ` — ${state}`}
            </p>
          )}


        </div>
      </section>

      <section className="mot__video-details bg-black text-white">
        <article className="inner grid grid-cols-1 md:grid-cols-3 gap-12 justify-between">
          <div className="col-span-1 relative">
            <div className="md:sticky md:top-32 video-frame aspect-3/4 relative mx-auto bg-white/5 mt-2">
              <iframe
                src={`https://player.vimeo.com/video/${video.uri.split('/')[2]}?dnt=1&transparent=0&autoplay=0&muted=0&loop=0&title=0&byline=0&portrait=0`}
                width="auto"
                height="auto"
                className="w-full h-full aspect-3/4"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={video.name}
                loading="lazy"
              />
            </div>
          </div>
          <div className="col-span-1 md:col-span-2">
            <div className="grid grid-cols-3">
              <header className="col-span-2">
                <h2
                  className="text-base md:text-lg font-medium leading-5 whitespace-pre-wrap pr-10 md:pr-20"
                  dangerouslySetInnerHTML={{ __html: video.description }}
                />
              </header>
              <div className="buttons-group col-span-1">
                <div className="sticky top-32 flex flex-col gap-3">
                  <button
                    onClick={handleButtonClick}
                    className={`voting-button-secondary flex text-center items-center justify-center gap-1.5 md:gap-3 ${
                      notificationVisible
                        ? 'bg-black text-white'
                        : 'bg-white text-black'
                    }`}
                  >
                    {notificationVisible ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2.5}
                        stroke="currentColor"
                        className="size-4 relative top-[2.5px]"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m4.5 12.75 6 6 9-13.5"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="size-4 relative top-[2px]"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
                        />
                      </svg>
                    )}
                    {buttonText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>
      </section>

      <section className="mot__details bg-black text-white">
        <div className="inner pt-10">
          <div className="col-span-3 flex flex-col md:flex-row justify-center mt-12 md:mt-20 gap-4 px-4 sm:px-6 lg:px-8">
            <Link
              className="voting-button-secondary flex gap-4 items-center"
              to="/videos/list"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-6 relative top-[3px]"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18"
                />
              </svg>
              Zurück zur Übersicht
            </Link>
            <button
              className="voting-button-secondary flex gap-4 items-center"
              onClick={handleRandomVideo}
            >
              Zufälliges Video laden
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-6 rotate-180 relative top-[3px]"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18"
                />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <Footer videoList={true} data={data} />
    </Layout>
  );
};

export const query = graphql`
  query {
    strapiMotSetting {
      countdown
      countdownDate
      countdownText
      headerBtn
      headerBtnLink
      headerBtnText
      nomination
      voting
      id
      partnerHeadline
      partnerDescription
      FooterMenu {
        subpageSlug
        subpageTitle
      }
      NominationSuccessPage {
        subpageSlug
        subpageTitle
      }
      UploadSuccessPage {
        subpageSlug
        subpageTitle
      }
      Partner {
        galeryDesc
        galeryHeadline
        id
        galeryOverlayText
        galeryMedia {
          caption
          alternativeText
          mime
          name
          url
          file {
            childImageSharp {
              gatsbyImageData(
                quality: 100
                layout: FULL_WIDTH
                placeholder: DOMINANT_COLOR
              )
            }
          }
        }
      }
    }
  }
`;

export function Head({ pageContext }) {
  const { video } = pageContext;
  const { name, tags } = video;

  // Extract internal names from the tags
  const { name: name1, name2, name3 } = extractInternalTags(tags);

  // Helper function to format names with commas and "und"
  const formatNames = (namesArray) => {
    const names = namesArray.filter(Boolean);
    if (names.length > 1) {
      return names.slice(0, -1).join(', ') + ' und ' + names[names.length - 1];
    }
    return names.join('');
  };

  // Generate dynamic title and description with formatted names
  const formattedNames = formatNames([name1, name2, name3]);
  const title = `${formattedNames} — Movers of Tomorrow Awards`;
  const description = `Schau dir das inspirierende Video von ${formattedNames} an und gib deine Stimme bis zum 30. September ab.`;

  const imageUrl = "https://res.cloudinary.com/allianz-foundation/image/upload/v1720102007/Moversof_Tomorrow_og_image_5a49773177.png";

  return (
    <>
      <html lang="de" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="image" content={imageUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="robots" content="index,follow" />
      <meta name="googlebot" content="index,follow" />
    </>
  );
}

export default VideoTemplate;
